import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api.js';
import type { SearchResponse } from './types.js';
import { useDebounced } from './use-debounced.js';

export const useGlobalSearch = () => {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debouncedQ = useDebounced(q.trim(), 300);

  const search = useQuery({
    queryKey: ['admin-search', debouncedQ],
    queryFn: () => api.request<SearchResponse>('/v1/admin/search', { query: { q: debouncedQ } }),
    enabled: debouncedQ.length >= 2,
    staleTime: 30_000,
  });

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
      if (e.key === 'Escape') {
        setOpen(false);
        inputRef.current?.blur();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const close = () => {
    setOpen(false);
    setQ('');
  };

  const showDropdown = open && debouncedQ.length >= 2;
  const totalResults =
    (search.data?.owners.length ?? 0) +
    (search.data?.lots.length ?? 0) +
    (search.data?.operators.length ?? 0);

  return {
    q,
    setQ,
    open,
    setOpen,
    containerRef,
    inputRef,
    debouncedQ,
    search,
    close,
    showDropdown,
    totalResults,
  };
};
